import { cn } from "@tine/designsystem-utils";
import isString from "lodash-es/isString";
import { observer } from "mobx-react-lite";
import type { MouseEventHandler, ReactElement } from "react";

type Justification = "start" | "end" | "center" | "between" | "around";
type Alignment = "start" | "end" | "center" | "stretch" | "baseline";

type InfoLineProps = {
   label?: string | null;
   labelClassName?: string;
   value: string | number | null | undefined | ReactElement;
   valueClassName?: string;
   className?: string;
   align?: Justification;
   valign?: Alignment;
   margin?: boolean;
   rowOnMobile?: boolean;
   onClick?: MouseEventHandler<HTMLDivElement>;
};

const justifyClassName: { [alignment in Justification]: string } = {
   start: "justify-start",
   around: "justify-around",
   between: "justify-between",
   end: "justify-end",
   center: "justify-center"
};

const lgAlignItemsClassName: { [alignment in Alignment]: string } = {
   start: "lg:items-start",
   stretch: "lg:items-stretch",
   end: "lg:items-end",
   center: "lg:items-center",
   baseline: "lg:items-baseline"
};

/**
 * The InfoLine element displays a row of data with a label and a value.
 * On desktop the label is shown on the same line as the value.
 * On mobile the label is shown above the line with the value because the label and value often take too much horisontal space.
 */
const InfoLine = ({
   label,
   labelClassName = "",
   value,
   valueClassName = "",
   className = "",
   align = "between",
   valign = "center",
   margin = false,
   rowOnMobile = false,
   onClick = () => {}
}: InfoLineProps): ReactElement => {
   const classNames = cn(`flex ${justifyClassName[align]}`, className, {
      [`flex-col flex-wrap items-start lg:flex-row ${lgAlignItemsClassName[valign]}`]: !rowOnMobile,
      "mb-2": margin
   });

   return (
      // biome-ignore lint/a11y/noStaticElementInteractions: maybe this should be a button when an onClick is provided?
      <div className={classNames} onClick={onClick} data-testid="info-line">
         <div className={`lg:pr-1 ${labelClassName}`}>{isString(label) ? `${label}:` : ""}</div>
         <div
            className={`lg:pl-1 lg:text-right ${valueClassName} ${isString(value) ? "font-bold" : ""}`}
            data-testid="info-line-value"
         >
            {value ?? ""}
         </div>
      </div>
   );
};

export default observer(InfoLine);
